const DEFAULT_STALL_TIMEOUT_MS = 15_000
const MIN_EXTERNAL_FALLBACK_DELAY_MS = 500
const MEDIA_TIME_EPSILON = 0.000_001
const FINGERPRINT_WIDTH = 16
const FINGERPRINT_HEIGHT = 9

export type FrameFreshness = 'callback' | 'clock' | 'fingerprint'

export interface FrameIdentity {
  readonly source: 'video-callback' | 'watchdog' | 'external'
  readonly callbackSequence: number
  readonly presentedFrames: number | null
  readonly totalVideoFrames: number | null
  readonly currentTime: number | null
  readonly fingerprint: number | null
}

export interface FrameSchedulerHealth {
  readonly running: boolean
  readonly processing: boolean
  readonly stalled: boolean
  readonly startedAt: number | null
  readonly lastVideoFrameAt: number | null
  readonly lastSuccessfulAt: number | null
}

export interface FrameSchedulerOptions {
  readonly stallTimeoutMs?: number
  readonly externalFallbackDelayMs?: number
  readonly initialMediaTime?: number | null
  readonly now?: () => number
  readonly onStalled?: (health: FrameSchedulerHealth) => void
  readonly frameFingerprint?: () => number | null
}

type FrameProcessor = (
  capturedAt: number,
  signal: AbortSignal,
  identity: FrameIdentity,
  freshness: FrameFreshness,
) => Promise<boolean>

export class FrameScheduler {
  private running = false
  private processing = false
  private callbackId: number | null = null
  private watchdogTimerId: number | null = null
  private lastRunAt = -Infinity
  private startedAt: number | null = null
  private lastVideoFrameAt: number | null = null
  private lastSuccessfulAt: number | null = null
  private lastPresentedFrames: number | null = null
  private lastTotalVideoFrames: number | null = null
  private lastCurrentTime: number | null = null
  private lastFingerprint: number | null = null
  private callbackSequence = 0
  private processingTask: Promise<boolean> | null = null
  private processingController: AbortController | null = null
  private processor: FrameProcessor | null = null
  private stallReported = false
  private heartbeatWorker: Worker | null = null
  private readonly visibilityHandler = () => this.updateHiddenHeartbeat()

  constructor(
    private readonly video: HTMLVideoElement,
    private intervalMs = 250,
    private readonly options: FrameSchedulerOptions = {},
  ) {}

  setInterval(intervalMs: number): void {
    this.intervalMs = Math.max(16, intervalMs)
  }

  start(processFrame: FrameProcessor): void {
    if (this.running) return
    this.running = true
    this.processor = processFrame
    this.startedAt = this.now()
    this.lastRunAt = -Infinity
    this.lastVideoFrameAt = null
    this.lastSuccessfulAt = null
    this.lastPresentedFrames = null
    this.lastTotalVideoFrames = null
    this.lastCurrentTime = this.finite(this.options.initialMediaTime)
    this.lastFingerprint = null
    this.callbackSequence = 0
    this.stallReported = false
    this.scheduleVideoCallback()
    this.scheduleWatchdog()
    document.addEventListener('visibilitychange', this.visibilityHandler)
    this.updateHiddenHeartbeat()
  }

  /** Drives capture from a companion window or hidden-page heartbeat. */
  pulse(force = false): boolean {
    if (!this.running) return false
    return this.tryFallback(this.now(), 'external', force)
  }

  health(at = this.now()): FrameSchedulerHealth {
    const reference = this.lastSuccessfulAt ?? this.startedAt
    return {
      running: this.running,
      processing: this.processing,
      stalled: Boolean(this.running && reference !== null && at - reference >= this.stallTimeoutMs()),
      startedAt: this.startedAt,
      lastVideoFrameAt: this.lastVideoFrameAt,
      lastSuccessfulAt: this.lastSuccessfulAt,
    }
  }

  stop(): void {
    this.running = false
    this.processor = null
    this.processingController?.abort()
    if (this.callbackId !== null) this.video.cancelVideoFrameCallback?.(this.callbackId)
    if (this.watchdogTimerId !== null) window.clearTimeout(this.watchdogTimerId)
    this.callbackId = null
    this.watchdogTimerId = null
    document.removeEventListener('visibilitychange', this.visibilityHandler)
    this.stopHiddenHeartbeat()
  }

  async waitForIdle(): Promise<void> {
    await this.processingTask
  }

  private scheduleVideoCallback(): void {
    if (!this.running || !this.video.requestVideoFrameCallback || this.callbackId !== null) return
    this.callbackId = this.video.requestVideoFrameCallback((_now, metadata) => {
      this.callbackId = null
      if (!this.running) return
      const now = this.now()
      this.lastVideoFrameAt = now
      const identity: FrameIdentity = {
        source: 'video-callback',
        callbackSequence: ++this.callbackSequence,
        presentedFrames: this.finite(metadata.presentedFrames),
        totalVideoFrames: this.totalVideoFrames(),
        currentTime: this.finite(metadata.mediaTime) ?? this.finite(this.video.currentTime),
        fingerprint: null,
      }
      if (!this.reportStallIfNeeded(now)) this.tryProcess(now, identity, 'callback')
      this.scheduleVideoCallback()
    })
  }

  private scheduleWatchdog(): void {
    if (!this.running || this.watchdogTimerId !== null) return
    this.watchdogTimerId = window.setTimeout(() => {
      this.watchdogTimerId = null
      if (!this.running) return
      this.tryFallback(this.now(), 'watchdog', false)
      this.scheduleWatchdog()
    }, Math.max(100, Math.min(500, this.intervalMs / 2)))
  }

  private tryFallback(now: number, source: 'watchdog' | 'external', force: boolean): boolean {
    if (this.reportStallIfNeeded(now)) return false
    const videoCallbackReference = this.lastVideoFrameAt ?? this.startedAt
    if (!force
      && videoCallbackReference !== null
      && now - videoCallbackReference < this.externalFallbackDelayMs()) return false

    const presentedFrames = this.presentedFrames()
    const totalVideoFrames = this.totalVideoFrames()
    const currentTime = this.finite(this.video.currentTime)
    const clockAdvanced = this.increased(presentedFrames, this.lastPresentedFrames)
      || this.increased(totalVideoFrames, this.lastTotalVideoFrames)
      || this.increased(currentTime, this.lastCurrentTime, MEDIA_TIME_EPSILON)
    let fingerprint: number | null = null
    let freshness: FrameFreshness = 'clock'

    if (!clockAdvanced) {
      fingerprint = this.videoFingerprint()
      if (fingerprint === null) return false
      if (this.lastFingerprint === null && this.lastSuccessfulAt !== null && !force) {
        this.lastFingerprint = fingerprint
        return false
      }
      if (this.lastFingerprint === fingerprint && !force) return false
      freshness = 'fingerprint'
    }

    return this.tryProcess(now, {
      source,
      callbackSequence: this.callbackSequence,
      presentedFrames,
      totalVideoFrames,
      currentTime,
      fingerprint,
    }, freshness)
  }

  private tryProcess(now: number, identity: FrameIdentity, freshness: FrameFreshness): boolean {
    const processor = this.processor
    if (!processor || this.processing || now - this.lastRunAt < this.intervalMs) return false

    this.processing = true
    this.lastRunAt = now
    const controller = new AbortController()
    this.processingController = controller
    const task = Promise.resolve().then(() => processor(Date.now(), controller.signal, identity, freshness))
    this.processingTask = task
    void task.then(
      (successful) => {
        if (!successful || controller.signal.aborted) return
        this.lastSuccessfulAt = this.now()
        this.lastPresentedFrames = identity.presentedFrames ?? this.lastPresentedFrames
        this.lastTotalVideoFrames = identity.totalVideoFrames ?? this.lastTotalVideoFrames
        this.lastCurrentTime = identity.currentTime ?? this.lastCurrentTime
        if (identity.fingerprint !== null) this.lastFingerprint = identity.fingerprint
        this.stallReported = false
      },
      () => undefined,
    ).finally(() => {
      if (this.processingTask !== task) return
      this.processingTask = null
      this.processingController = null
      this.processing = false
    })
    return true
  }

  private reportStallIfNeeded(now: number): boolean {
    const health = this.health(now)
    if (!health.stalled) return false
    if (!this.stallReported) {
      this.stallReported = true
      this.options.onStalled?.(health)
    }
    return true
  }

  private videoFingerprint(): number | null {
    if (this.options.frameFingerprint) return this.options.frameFingerprint()
    if (!this.video.videoWidth || !this.video.videoHeight || this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return null
    try {
      const canvas = new OffscreenCanvas(FINGERPRINT_WIDTH, FINGERPRINT_HEIGHT)
      const context = canvas.getContext('2d', { willReadFrequently: true })
      if (!context) return null
      context.drawImage(this.video, 0, 0, FINGERPRINT_WIDTH, FINGERPRINT_HEIGHT)
      const data = context.getImageData(0, 0, FINGERPRINT_WIDTH, FINGERPRINT_HEIGHT).data
      let hash = 2_166_136_261
      for (let index = 0; index < data.length; index += 16) {
        hash ^= (data[index] ?? 0) + ((data[index + 1] ?? 0) << 8) + ((data[index + 2] ?? 0) << 16)
        hash = Math.imul(hash, 16_777_619)
      }
      return hash >>> 0
    } catch {
      return null
    }
  }

  private updateHiddenHeartbeat(): void {
    if (!this.running || document.visibilityState !== 'hidden') {
      this.stopHiddenHeartbeat()
      return
    }
    if (this.heartbeatWorker || typeof Worker === 'undefined' || typeof URL.createObjectURL !== 'function') return
    try {
      const url = URL.createObjectURL(new Blob([
        'const timer=setInterval(()=>postMessage(0),1000);onmessage=()=>{clearInterval(timer);close()}',
      ], { type: 'text/javascript' }))
      const worker = new Worker(url)
      URL.revokeObjectURL(url)
      worker.onmessage = () => {
        if (this.running && document.visibilityState === 'hidden') this.tryFallback(this.now(), 'external', false)
      }
      this.heartbeatWorker = worker
    } catch {
      this.heartbeatWorker = null
    }
  }

  private stopHiddenHeartbeat(): void {
    const worker = this.heartbeatWorker
    this.heartbeatWorker = null
    if (!worker) return
    try {
      worker.postMessage('stop')
      worker.terminate()
    } catch {
      // Hidden-page scheduling is best effort.
    }
  }

  private presentedFrames(): number | null {
    const value = (this.video as HTMLVideoElement & { webkitDecodedFrameCount?: number }).webkitDecodedFrameCount
    return this.finite(value)
  }

  private totalVideoFrames(): number | null {
    try {
      return this.finite(this.video.getVideoPlaybackQuality?.().totalVideoFrames)
    } catch {
      return null
    }
  }

  private increased(value: number | null, previous: number | null, epsilon = 0): boolean {
    return value !== null && (previous === null || value > previous + epsilon)
  }

  private finite(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  }

  private externalFallbackDelayMs(): number {
    return Math.max(
      this.options.externalFallbackDelayMs ?? MIN_EXTERNAL_FALLBACK_DELAY_MS,
      this.intervalMs * 2,
    )
  }

  private stallTimeoutMs(): number {
    return Math.max(1_000, this.options.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT_MS)
  }

  private now(): number {
    return this.options.now?.() ?? Date.now()
  }
}
